import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { ArrowLeft, HardDrive, Database, Users, Gauge, RefreshCw, Loader, Info, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  green: { label: "Todo va bien", color: "#5A8060", bg: "#95B89A" },
  yellow: { label: "Conviene vigilar", color: "#B7791F", bg: "#E8B04B" },
  red: { label: "Hora de ampliar", color: "#c44a3a", bg: "#c44a3a" },
  unknown: { label: "Sin datos todavía", color: "#5B3E2E99", bg: "#5B3E2E" },
};

const fmtBytes = (b) => {
  if (b === null || b === undefined) return "—";
  if (b < 0) return `-${fmtBytes(-b)}`;
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toLocaleString("es-ES", { maximumFractionDigits: 2 })} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toLocaleString("es-ES", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(0, Math.round(b / 1024)).toLocaleString("es-ES")} KB`;
};
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v.toLocaleString("es-ES")} %`);

function StatusPill({ status, testid }) {
  const s = STATUS[status] || STATUS.unknown;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ color: s.color, backgroundColor: `${s.bg}1F` }}>
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.bg }} />
      {s.label}
    </span>
  );
}

function Meter({ pct, status, testid }) {
  const s = STATUS[status] || STATUS.unknown;
  return (
    <div className="h-3 rounded-full bg-[#5B3E2E]/8 overflow-hidden" data-testid={testid}>
      <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.min(100, pct || 0)}%`, backgroundColor: s.bg }} />
    </div>
  );
}

function Section({ icon: Icon, title, status, children, testid }) {
  return (
    <section data-testid={testid} className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-5 mb-4 animate-slide-up">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#E48D57]/10 flex items-center justify-center"><Icon size={18} className="text-[#E48D57]" /></div>
          <h2 className="font-heading text-lg text-[#5B3E2E]">{title}</h2>
        </div>
        {status && <StatusPill status={status} testid={`${testid}-status`} />}
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-[#5B3E2E]/60">{label}</span>
      <span data-testid={testid} className="font-medium text-[#5B3E2E]">{value}</span>
    </div>
  );
}

function Advice({ children, tone = "info" }) {
  const warn = tone === "warn";
  return (
    <p className={`text-[12px] leading-relaxed mt-3 p-3 rounded-xl flex gap-2 ${warn ? "bg-[#c44a3a]/8 text-[#9a3a2d]" : "bg-[#5B3E2E]/5 text-[#5B3E2E]/70"}`}>
      {warn ? <AlertTriangle size={14} className="shrink-0 mt-0.5" /> : <Info size={14} className="shrink-0 mt-0.5" />}
      <span>{children}</span>
    </p>
  );
}

const FOLDER_LABELS = { feed: "Publicaciones del inicio", rooms: "Fotos, vídeos y audios de salas", avatars: "Fotos de perfil" };

function StorageCard({ s }) {
  if (s.error) return <Section icon={HardDrive} title="Archivos" status="unknown" testid="health-storage"><Advice>{s.error}</Advice></Section>;
  const folders = Object.entries(s.by_folder || {}).sort((a, b) => b[1].bytes - a[1].bytes);
  return (
    <Section icon={HardDrive} title="Archivos (vídeos, audios, fotos)" status={s.status} testid="health-storage">
      <div className="flex items-end justify-between mb-2">
        <p className="text-2xl font-heading font-bold text-[#5B3E2E]" data-testid="health-storage-used">{fmtBytes(s.account_bytes)}</p>
        <p className="text-xs text-[#5B3E2E]/50">de {fmtBytes(s.quota_bytes)} · {fmtPct(s.used_pct)}</p>
      </div>
      <Meter pct={s.used_pct} status={s.status} testid="health-storage-meter" />
      <div className="mt-4">
        {folders.map(([k, v]) => <Row key={k} label={`${FOLDER_LABELS[k] || k} (${v.files})`} value={fmtBytes(v.bytes)} testid={`health-storage-folder-${k}`} />)}
        {s.other_apps_bytes > 0 && <Row label="Otras apps de tu cuenta" value={fmtBytes(s.other_apps_bytes)} testid="health-storage-other-apps" />}
      </div>
      {s.status === "red" && <Advice tone="warn">Has pasado del 80 % del espacio. Es el momento de llevar los vídeos a un almacenamiento propio (por ejemplo, AWS S3) antes de que no se puedan subir más archivos.</Advice>}
      {s.status === "yellow" && <Advice>Has pasado de la mitad del espacio. Ve preparando el almacenamiento propio para los vídeos.</Advice>}
      {s.status === "green" && <Advice>Tienes espacio de sobra. El aviso se pondrá en rojo al llegar al 80 %.</Advice>}
    </Section>
  );
}

function DatabaseCard({ d }) {
  if (d.error) return <Section icon={Database} title="Base de datos" status="unknown" testid="health-database"><Advice>{d.error}</Advice></Section>;
  const growth = d.month_growth_bytes;
  return (
    <Section icon={Database} title="Base de datos" status={d.status} testid="health-database">
      <p className="text-2xl font-heading font-bold text-[#5B3E2E] mb-1" data-testid="health-db-size">{fmtBytes(d.total_bytes)}</p>
      <p className="text-xs text-[#5B3E2E]/50 mb-3" data-testid="health-db-growth">
        {growth === null ? "El crecimiento del mes se mostrará a partir de mañana." : `${growth >= 0 ? "+" : ""}${fmtBytes(growth)} desde el ${new Date(d.month_growth_since).toLocaleDateString("es-ES", { day: "numeric", month: "long" })}`}
      </p>
      {Object.entries(d.counts || {}).map(([k, v]) => <Row key={k} label={v.label} value={v.count.toLocaleString("es-ES")} testid={`health-db-count-${k}`} />)}
      <Advice>Aquí solo se guarda texto (usuarios, mensajes, reservas). Los vídeos y fotos van aparte, por eso crece muy despacio.</Advice>
    </Section>
  );
}

function OnlineCard({ o }) {
  const max = Math.max(1, ...o.history.map((h) => h.peak));
  return (
    <Section icon={Users} title="Personas conectadas" testid="health-online">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-[#95B89A]/10 rounded-2xl p-3"><p className="text-[11px] text-[#5B3E2E]/55">Ahora en salas</p><p className="text-2xl font-heading font-bold text-[#5B3E2E]" data-testid="health-online-now">{o.now}</p></div>
        <div className="bg-[#E48D57]/10 rounded-2xl p-3"><p className="text-[11px] text-[#5B3E2E]/55">Máximo de hoy</p><p className="text-2xl font-heading font-bold text-[#5B3E2E]" data-testid="health-online-peak">{o.peak_today}</p></div>
      </div>
      <p className="text-[10px] tracking-[0.15em] uppercase text-[#5B3E2E]/40 font-semibold mb-2">Máximo a la vez · últimos 14 días</p>
      <div className="flex items-end gap-1 h-20" data-testid="health-online-chart">
        {o.history.map((h) => (
          <div key={h.date} className="flex-1 flex flex-col items-center justify-end h-full" title={`${h.date}: ${h.peak}`}>
            <div className="w-full rounded-t-md bg-[#95B89A]" style={{ height: `${Math.max(4, (h.peak / max) * 100)}%`, opacity: h.peak ? 1 : 0.25 }} />
          </div>
        ))}
      </div>
      <Advice>Cuenta las personas con una sala de chat abierta. Se registra desde el {new Date(o.tracking_since).toLocaleDateString("es-ES", { day: "numeric", month: "long" })}.</Advice>
    </Section>
  );
}

function PerformanceCard({ p }) {
  const lv = p.levels || {};
  const worstText = p.status === "red"
    ? "La app va justa de recursos. Es momento de subir el plan de publicación."
    : p.status === "yellow"
      ? "Algún indicador está alto. Si se repite varios días, plantéate subir el plan de publicación."
      : "La app responde rápido y le sobran recursos.";
  return (
    <Section icon={Gauge} title="Velocidad y memoria" status={p.status} testid="health-performance">
      <Row label="Tiempo de respuesta (95 % de las veces)" value={p.p95_ms === null ? "—" : `${p.p95_ms.toLocaleString("es-ES")} ms`} testid="health-perf-p95" />
      <Row label="Errores del servidor (15 min)" value={fmtPct(p.error_pct)} testid="health-perf-errors" />
      <div className="py-1.5">
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="text-[#5B3E2E]/60">Memoria en uso</span>
          <span className="font-medium text-[#5B3E2E]" data-testid="health-perf-memory">{fmtBytes(p.memory_used_bytes)} de {fmtBytes(p.memory_limit_bytes)}</span>
        </div>
        <Meter pct={p.memory_pct} status={lv.memory} testid="health-perf-memory-meter" />
      </div>
      <Row label="Procesador (máximo última hora)" value={fmtPct(p.cpu_peak_pct_1h)} testid="health-perf-cpu" />
      <Advice tone={p.status === "red" ? "warn" : "info"}>{worstText}</Advice>
    </Section>
  );
}

function ReliabilityNote({ exact }) {
  return (
    <div data-testid="health-reliability" className="rounded-3xl border border-dashed border-[#5B3E2E]/20 p-5 text-[12px] text-[#5B3E2E]/65 leading-relaxed space-y-2">
      <p className="font-semibold text-[#5B3E2E]">¿De dónde salen estos datos?</p>
      <p><strong>Archivos:</strong> se suman uno a uno todos los archivos guardados en tu cuenta. {exact ? "Es la cifra exacta." : "Hay tantos archivos que la cifra es aproximada (por debajo de la real)."} Se vuelve a medir cada 30 minutos o al pulsar «Actualizar».</p>
      <p><strong>Base de datos:</strong> tamaño real que informa la propia base de datos.</p>
      <p><strong>Velocidad, memoria y conectados:</strong> se miden en el servidor en tiempo real. Si en el futuro la app funciona con varios servidores a la vez, estas cifras serán las del servidor que responde.</p>
    </div>
  );
}

export default function PlatformHealthPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/platform-health`, { params: { refresh }, withCredentials: true });
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    load();
    const t = setInterval(() => load(), 60000);
    return () => clearInterval(t);
  }, [isAdmin, load]);

  if (!isAdmin) {
    return <div className="max-w-lg mx-auto px-4 py-12 text-center"><p className="text-[#5B3E2E]/60">No tienes acceso a esta página</p></div>;
  }

  return (
    <div data-testid="platform-health-page" className="max-w-lg mx-auto px-4 py-6">
      <button data-testid="health-back-btn" onClick={() => navigate("/app/admin")} className="flex items-center gap-2 text-[#5B3E2E]/60 hover:text-[#5B3E2E] mb-6">
        <ArrowLeft size={18} /><span className="text-sm">Volver al panel</span>
      </button>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h1 className="font-heading text-2xl text-[#5B3E2E]">Estado de la plataforma</h1>
        <button data-testid="health-refresh-btn" onClick={() => load(true)} disabled={refreshing} className="shrink-0 flex items-center gap-1.5 text-xs font-medium text-[#E48D57] px-3 py-2 rounded-full bg-[#E48D57]/10 hover:bg-[#E48D57]/20 transition-colors disabled:opacity-50">
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader className="animate-spin text-[#E48D57]" /></div>
      ) : !data ? (
        <p data-testid="health-error" className="text-sm text-[#c44a3a] py-8">No se pudieron cargar los datos. Inténtalo de nuevo en un momento.</p>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-6">
            <StatusPill status={data.status} testid="health-overall-status" />
            <span className="text-[11px] text-[#5B3E2E]/45">Actualizado a las {new Date(data.generated_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <StorageCard s={data.storage} />
          <PerformanceCard p={data.performance} />
          <OnlineCard o={data.online} />
          <DatabaseCard d={data.database} />
          <ReliabilityNote exact={data.storage?.exact !== false} />
        </>
      )}
    </div>
  );
}
